import React, { useState } from 'react';
import {
  Card,
  Select,
  Typography,
  Space,
  Button,
  Tooltip,
  Row,
  Col,
} from 'antd';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import {
  BarChartOutlined,
  LineChartOutlined,
  InfoCircleOutlined,
  RiseOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

interface EnhancedRadarChartProps {
  brandData: any;
  industryAverage?: any;
  showComparison?: boolean;
  showTrend?: boolean;
}

const EnhancedRadarChart: React.FC<EnhancedRadarChartProps> = ({
  brandData,
  industryAverage,
  showComparison = true,
  showTrend = false,
}) => {
  const [chartType, setChartType] = useState<'radar' | 'trend'>('radar');
  const [selectedPeriod, setSelectedPeriod] = useState('6months');

  // 模拟历史趋势数据
  const trendData = [
    {
      month: '2024-03',
      environmental: 3.2,
      labor: 3.8,
      animal_welfare: 4.1,
      community: 3.5,
      transparency: 3.0,
    },
    {
      month: '2024-04',
      environmental: 3.4,
      labor: 3.9,
      animal_welfare: 4.2,
      community: 3.6,
      transparency: 3.2,
    },
    {
      month: '2024-05',
      environmental: 3.6,
      labor: 4.0,
      animal_welfare: 4.2,
      community: 3.8,
      transparency: 3.4,
    },
    {
      month: '2024-06',
      environmental: 3.8,
      labor: 4.1,
      animal_welfare: 4.3,
      community: 4.0,
      transparency: 3.6,
    },
    {
      month: '2024-07',
      environmental: 4.0,
      labor: 4.2,
      animal_welfare: 4.4,
      community: 4.1,
      transparency: 3.8,
    },
    {
      month: '2024-08',
      environmental: 4.1,
      labor: 4.3,
      animal_welfare: 4.5,
      community: 4.2,
      transparency: 4.0,
    },
  ];

  const radarData = [
    {
      dimension: '环境影响',
      brand: brandData?.environmental_score || 0,
      industry: industryAverage?.environmental_score || 3.0,
      fullMark: 5,
    },
    {
      dimension: '劳工权益',
      brand: brandData?.labor_score || 0,
      industry: industryAverage?.labor_score || 3.2,
      fullMark: 5,
    },
    {
      dimension: '动物福利',
      brand: brandData?.animal_welfare_score || 0,
      industry: industryAverage?.animal_welfare_score || 2.8,
      fullMark: 5,
    },
    {
      dimension: '社区影响',
      brand: brandData?.community_impact_score || 0,
      industry: industryAverage?.community_impact_score || 3.1,
      fullMark: 5,
    },
    {
      dimension: '透明度',
      brand: brandData?.transparency_score || 0,
      industry: industryAverage?.transparency_score || 2.9,
      fullMark: 5,
    },
  ];

  const getScoreColor = (score: number) => {
    if (score >= 4) return '#52c41a';
    if (score >= 3) return '#faad14';
    if (score >= 2) return '#fa8c16';
    return '#ff4d4f';
  };

  const getScoreLevel = (score: number) => {
    if (score >= 4.5) return '优秀';
    if (score >= 4) return '良好';
    if (score >= 3) return '一般';
    if (score >= 2) return '较差';
    return '很差';
  };

  const renderRadarChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <RadarChart data={radarData}>
        <PolarGrid />
        <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 12 }} />
        <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10 }} />
        <Radar
          name="品牌评分"
          dataKey="brand"
          stroke="var(--app-primary-color)"
          fill="var(--app-primary-color)"
          fillOpacity={0.3}
          strokeWidth={2}
        />
        {showComparison && (
          <Radar
            name="行业平均"
            dataKey="industry"
            stroke="#ff7875"
            fill="#ff7875"
            fillOpacity={0.1}
            strokeWidth={1}
            strokeDasharray="5 5"
          />
        )}
        <Legend />
      </RadarChart>
    </ResponsiveContainer>
  );

  const renderTrendChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={trendData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" />
        <YAxis domain={[0, 5]} />
        <Tooltip />
        <Legend />
        <Line
          type="monotone"
          dataKey="environmental"
          stroke="#52c41a"
          name="环境影响"
          strokeWidth={2}
        />
        <Line
          type="monotone"
          dataKey="labor"
          stroke="#faad14"
          name="劳工权益"
          strokeWidth={2}
        />
        <Line
          type="monotone"
          dataKey="animal_welfare"
          stroke="#eb2f96"
          name="动物福利"
          strokeWidth={2}
        />
        <Line
          type="monotone"
          dataKey="community"
          stroke="#722ed1"
          name="社区影响"
          strokeWidth={2}
        />
        <Line
          type="monotone"
          dataKey="transparency"
          stroke="#13c2c2"
          name="透明度"
          strokeWidth={2}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderScoreBreakdown = () => (
    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
      {radarData.map((item, index) => {
        const score = item.brand;
        const industryScore = item.industry;
        const difference = score - industryScore;

        return (
          <Col span={12} key={index}>
            <Card size="small" style={{ height: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <Text strong>{item.dimension}</Text>
                <Tooltip
                  title={`评分来源：用户评价 (40%) + 第三方认证 (35%) + 公开数据 (25%)`}
                >
                  <InfoCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </div>

              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text
                    style={{
                      fontSize: 20,
                      fontWeight: 'bold',
                      color: getScoreColor(score),
                    }}
                  >
                    {score.toFixed(1)}
                  </Text>
                  <Text type="secondary">/5</Text>
                  <Text
                    style={{
                      fontSize: 12,
                      color: getScoreColor(score),
                      backgroundColor: getScoreColor(score) + '20',
                      padding: '2px 6px',
                      borderRadius: 4,
                    }}
                  >
                    {getScoreLevel(score)}
                  </Text>
                </div>
              </div>

              {showComparison && (
                <div style={{ fontSize: 12 }}>
                  <Text type="secondary">
                    行业平均: {industryScore.toFixed(1)}
                  </Text>
                  <Text
                    style={{
                      marginLeft: 8,
                      color: difference >= 0 ? '#52c41a' : '#ff4d4f',
                    }}
                  >
                    {difference >= 0 ? '+' : ''}
                    {difference.toFixed(1)}
                  </Text>
                </div>
              )}
            </Card>
          </Col>
        );
      })}
    </Row>
  );

  return (
    <Card
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Space>
            <BarChartOutlined />
            <span>道德评分分析</span>
          </Space>
          <Space>
            {showTrend && (
              <Select
                value={selectedPeriod}
                onChange={setSelectedPeriod}
                size="small"
                style={{ width: 120 }}
              >
                <Option value="3months">近3个月</Option>
                <Option value="6months">近6个月</Option>
                <Option value="1year">近1年</Option>
              </Select>
            )}
            <Space.Compact size="small">
              <Button
                type={chartType === 'radar' ? 'primary' : 'default'}
                icon={<BarChartOutlined />}
                onClick={() => setChartType('radar')}
              >
                雷达图
              </Button>
              {showTrend && (
                <Button
                  type={chartType === 'trend' ? 'primary' : 'default'}
                  icon={<LineChartOutlined />}
                  onClick={() => setChartType('trend')}
                >
                  趋势图
                </Button>
              )}
            </Space.Compact>
          </Space>
        </div>
      }
    >
      {chartType === 'radar' ? renderRadarChart() : renderTrendChart()}

      {chartType === 'radar' && renderScoreBreakdown()}

      {chartType === 'trend' && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            backgroundColor: '#f6ffed',
            borderRadius: 6,
            border: '1px solid #b7eb8f',
          }}
        >
          <Space>
            <RiseOutlined style={{ color: '#52c41a' }} />
            <Text style={{ fontSize: 12 }}>
              <Text strong>趋势分析：</Text>
              该品牌在过去6个月中，各维度评分均呈上升趋势，
              其中环境影响和透明度改善最为显著。
            </Text>
          </Space>
        </div>
      )}

      <div
        style={{
          marginTop: 16,
          padding: 12,
          backgroundColor: '#f0f0f0',
          borderRadius: 6,
          fontSize: 11,
          color: '#666',
        }}
      >
        <Text type="secondary">
          评分更新时间: {new Date().toLocaleDateString()} | 数据来源:
          用户评价、第三方认证机构、公开ESG报告 | 下次更新: 每月15日
        </Text>
      </div>
    </Card>
  );
};

export default EnhancedRadarChart;
